import React from 'react';
import { colors } from '../../constants/colors';
import { WEB_ANALYTICS_COLORS } from '../../constants/webAnalyticsTheme';
import DeviceBreakdownChart from './charts/DeviceBreakdownChart';
import EventBreakdownList from './charts/EventBreakdownList';

const formatNumber = (value) => (Number(value) || 0).toLocaleString();

const formatDuration = (ms, samples = 0) => {
  if (
    ms == null ||
    !Number.isFinite(Number(ms)) ||
    Number(ms) <= 0 ||
    !samples
  ) {
    return '0';
  }
  const totalSec = Math.round(Number(ms) / 1000);
  if (totalSec < 60) return `${totalSec} sec`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (s === 0) return `${m} min`;
  return `${m} min ${s} sec`;
};

const formatScrollDepth = (value) => {
  if (value == null) return '0';
  return `${value}%`;
};

const formatBounceRate = (summary = {}) => {
  const sessions = Number(summary.bounce_sessions) || 0;
  if (sessions <= 0) return null;
  const rate = Number(summary.bounce_rate);
  if (!Number.isFinite(rate)) return null;
  return `${Math.round(rate)}%`;
};

const PageDetailsModal = ({
  open,
  onClose,
  details,
  loading,
  error,
  days,
}) => {
  if (!open) return null;

  const summary = details?.summary || {};
  const referrers = details?.top_referrers || [];
  const maxRef = Math.max(...referrers.map((r) => r.count || 0), 1);
  const bounceDisplay = formatBounceRate(summary);
  const timeDisplay = formatDuration(
    summary.avg_time_on_page_ms,
    summary.avg_time_on_page_samples
  );
  const scrollDisplay = formatScrollDepth(summary.avg_scroll_depth);

  const kpis = [
    {
      label: 'Page Views',
      value: formatNumber(summary.page_views),
      accent: WEB_ANALYTICS_COLORS.page_views,
    },
    {
      label: 'Unique Visitors',
      value: formatNumber(summary.unique_visitors),
      accent: WEB_ANALYTICS_COLORS.unique_visitors,
    },
    {
      label: 'Avg. Time on Page',
      value: timeDisplay,
      accent: WEB_ANALYTICS_COLORS.time_on_page,
    },
    {
      label: 'Avg. Scroll Depth',
      value: scrollDisplay,
      accent: WEB_ANALYTICS_COLORS.scroll_depth,
    },
    ...(bounceDisplay
      ? [
          {
            label: 'Bounce Rate',
            value: bounceDisplay,
            accent: colors.status.warning,
          },
        ]
      : []),
    {
      label: 'Sessions',
      value: formatNumber(summary.sessions),
      accent: WEB_ANALYTICS_COLORS.sessions,
    },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(920px, 100%)',
          maxHeight: '90vh',
          overflow: 'auto',
          backgroundColor: colors.background.primary,
          borderRadius: '12px',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.2)',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 700,
                color: colors.text.primary,
                wordBreak: 'break-all',
              }}
            >
              {details?.path || 'Page details'}
            </h2>
            {details?.title ? (
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: '13px',
                  color: colors.text.secondary,
                }}
              >
                {details.title}
              </p>
            ) : null}
            <p
              style={{
                margin: '4px 0 0',
                fontSize: '12px',
                color: colors.text.light,
              }}
            >
              Last {days} days
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: `1px solid ${colors.borderVariants.light}`,
              background: colors.background.secondary,
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontWeight: 600,
              color: colors.text.primary,
              height: 'fit-content',
            }}
          >
            Close
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '12px 14px',
              marginBottom: '16px',
              borderRadius: '8px',
              backgroundColor: '#fef2f2',
              color: colors.status.error,
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {loading && !details ? (
          <div
            style={{
              padding: '48px',
              textAlign: 'center',
              color: colors.text.secondary,
            }}
          >
            Loading page details…
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '12px',
                marginBottom: '20px',
              }}
            >
              {kpis.map((kpi) => (
                <div
                  key={kpi.label}
                  style={{
                    padding: '14px',
                    borderRadius: '8px',
                    backgroundColor: colors.background.secondary,
                    borderLeft: `3px solid ${kpi.accent}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: colors.text.secondary,
                      marginBottom: '6px',
                    }}
                  >
                    {kpi.label}
                  </div>
                  <div
                    style={{
                      fontSize:
                        typeof kpi.value === 'string' && kpi.value.length > 12
                          ? '15px'
                          : '22px',
                      fontWeight: 700,
                      color: colors.text.primary,
                      lineHeight: 1.25,
                    }}
                  >
                    {kpi.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.borderVariants.light}`,
                  minHeight: 280,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h3
                  style={{
                    margin: '0 0 12px',
                    fontSize: '15px',
                    fontWeight: 700,
                    color: colors.text.primary,
                    flexShrink: 0,
                  }}
                >
                  Devices
                </h3>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <DeviceBreakdownChart
                    data={details?.by_device || []}
                    isLoading={loading}
                    compact
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.borderVariants.light}`,
                }}
              >
                <h3
                  style={{
                    margin: '0 0 12px',
                    fontSize: '15px',
                    fontWeight: 700,
                    color: colors.text.primary,
                  }}
                >
                  Referrers
                </h3>
                {referrers.length === 0 ? (
                  <div
                    style={{
                      padding: '32px 8px',
                      textAlign: 'center',
                      color: colors.text.secondary,
                      fontSize: '13px',
                    }}
                  >
                    No referrer data for this page
                  </div>
                ) : (
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                  >
                    {referrers.map((ref) => {
                      const widthPct = Math.max(
                        8,
                        Math.round(((ref.count || 0) / maxRef) * 100)
                      );
                      return (
                        <div key={ref.domain}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: 4,
                              fontSize: 13,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {ref.domain}
                            </span>
                            <span style={{ color: colors.text.secondary }}>
                              {formatNumber(ref.count)}
                            </span>
                          </div>
                          <div
                            style={{
                              height: 5,
                              borderRadius: 999,
                              backgroundColor: colors.background.tertiary,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${widthPct}%`,
                                height: '100%',
                                backgroundColor:
                                  WEB_ANALYTICS_COLORS.unique_visitors,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: '16px',
                borderRadius: '8px',
                border: `1px solid ${colors.borderVariants.light}`,
              }}
            >
              <h3
                style={{
                  margin: '0 0 12px',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: colors.text.primary,
                }}
              >
                Page Events
              </h3>
              <EventBreakdownList
                data={details?.event_breakdown || []}
                isLoading={loading}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PageDetailsModal;
